import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  // Use sessionStorage instead of localStorage - clears when browser/tab closes
  const [token, setToken] = useState(sessionStorage.getItem('admin_token'));

  useEffect(() => {
    if (token) {
      fetchAdminInfo();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Note: sessionStorage is automatically cleared when the browser tab/window closes,
  // so we don't add a `beforeunload` handler. Adding one would also fire on page refresh
  // (F5) and break deep-link refresh of authenticated routes.

  const fetchAdminInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin info:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
      });
      const { access_token } = response.data;
      sessionStorage.setItem('admin_token', access_token);
      setToken(access_token);
      
      // Fetch user info to determine role
      const userResponse = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      
      setAdmin(userResponse.data);
      return { success: true, role: userResponse.data.role, user: userResponse.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      await axios.post(`${API}/admin/register`, {
        username,
        email,
        password,
      });
      return await login(username, password);
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed',
      };
    }
  };

  const logout = () => {
    sessionStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
  };

  const getAuthHeader = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const value = useMemo(
    () => ({
      admin,
      user: admin, // Alias for compatibility
      loading,
      login,
      register,
      logout,
      getAuthHeader,
      isAuthenticated: !!admin,
    }),
    [admin, loading, getAuthHeader]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;